import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Car,
  FileText,
  Printer,
  Route,
  Search,
  UserCheck,
  Users,
} from 'lucide-react';
import apiService from '../../services/apiService';
import { downloadPdfBase64 } from '../../utils/pdfDownload';
import { EmptyState, LoadingState, MetricCard, PageHeader, PageShell, Panel } from '../ui/PageShell';
import { DataTable, TableShell } from '../ui/ActionMenu';
import { ui } from '../ui/styles';

const REPORT_ORDER = [
  'rides',
  'by_vehicle',
  'by_driver',
  'by_destination',
  'by_origin',
  'by_status',
  'by_transport_type',
  'passengers',
  'by_facility',
  'by_specialty',
  'occupancy',
  'incidents',
];

function monthStart() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function TransportReports() {
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState(todayIso);
  const [status, setStatus] = useState('');
  const [data, setData] = useState(null);
  const [activeReport, setActiveReport] = useState('rides');
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState('');
  const [searched, setSearched] = useState(false);

  const filters = useMemo(() => ({
    date_from: dateFrom,
    date_to: dateTo,
    status,
  }), [dateFrom, dateTo, status]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const result = await apiService.listTransportReports(filters);
      setData(result.data || null);
      setSearched(true);
      const firstWithRows = REPORT_ORDER.find((id) => (result.data?.relatorios?.[id]?.rows || []).length > 0);
      if (firstWithRows) setActiveReport(firstWithRows);
    } catch (error) {
      setData(null);
      toast.error(error.message || 'Erro ao carregar relatórios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrint = async (type) => {
    try {
      setPrinting(type);
      const toastId = toast.loading('Gerando relatório PDF...');
      const result = await apiService.printTransportReport({ ...filters, type });
      if (result.success && result.data?.arquivoBase64) {
        downloadPdfBase64(result.data.arquivoBase64, result.data.nomeArquivo);
        toast.success('Relatório gerado com sucesso', { id: toastId });
      } else {
        toast.error(result.error || 'Arquivo não disponível', { id: toastId });
      }
    } catch (error) {
      toast.error(error.message || 'Erro ao gerar PDF');
    } finally {
      setPrinting('');
    }
  };

  const handlePrintRoteiro = async (rideId) => {
    try {
      setPrinting(`roteiro-${rideId}`);
      const toastId = toast.loading('Gerando roteiro de passageiros...');
      const result = await apiService.printRideRoteiro(rideId);
      if (result.success && result.data?.arquivoBase64) {
        downloadPdfBase64(result.data.arquivoBase64, result.data.nomeArquivo);
        toast.success('Roteiro gerado com sucesso', { id: toastId });
      } else {
        toast.error(result.error || 'Arquivo não disponível', { id: toastId });
      }
    } catch (error) {
      toast.error(error.message || 'Erro ao gerar roteiro');
    } finally {
      setPrinting('');
    }
  };

  const reports = data?.relatorios || {};
  const current = reports[activeReport];
  const resumo = data?.resumo;

  return (
    <PageShell withToaster>
      <PageHeader
        title="Relatórios de corridas"
        description="Visualize e exporte os relatórios das corridas. A relação de passageiros com assinatura fica no roteiro de cada embarque."
      />

      <div className={ui.pageBody}>
        <Panel>
          <form
            className={ui.panelBody}
            onSubmit={(event) => {
              event.preventDefault();
              loadReports();
            }}
          >
            <div className={ui.filtersGrid}>
              <div>
                <label className={ui.label} htmlFor="date_from">Data inicial</label>
                <input
                  id="date_from"
                  type="date"
                  value={dateFrom}
                  onChange={(event) => setDateFrom(event.target.value)}
                  className={ui.input}
                />
              </div>
              <div>
                <label className={ui.label} htmlFor="date_to">Data final</label>
                <input
                  id="date_to"
                  type="date"
                  value={dateTo}
                  onChange={(event) => setDateTo(event.target.value)}
                  className={ui.input}
                />
              </div>
              <div>
                <label className={ui.label} htmlFor="status">Status</label>
                <select
                  id="status"
                  value={status}
                  onChange={(event) => setStatus(event.target.value)}
                  className={ui.select}
                >
                  <option value="">Todos</option>
                  <option value="scheduled">Agendadas</option>
                  <option value="completed">Concluídas</option>
                  <option value="cancelled">Canceladas</option>
                </select>
              </div>
            </div>
            <div className={ui.filtersActions}>
              <button type="submit" className={ui.btnPrimary} disabled={loading}>
                <Search size={16} />
                {loading ? 'Buscando...' : 'Buscar'}
              </button>
            </div>
          </form>
        </Panel>

        {loading && !data && <LoadingState message="Carregando relatórios..." />}

        {searched && resumo && (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard icon={Route} label="Corridas" value={resumo.corridas} tone="primary" />
            <MetricCard icon={Users} label="Passageiros" value={resumo.passageiros} tone="info" />
            <MetricCard icon={Car} label="Veículos" value={resumo.veiculos} tone="success" />
            <MetricCard icon={UserCheck} label="Motoristas" value={resumo.motoristas} tone="warning" />
          </div>
        )}

        {searched && data && (
          <div className="mt-6 grid gap-6 xl:grid-cols-[18rem_minmax(0,1fr)]">
            <Panel>
              <div className={ui.panelHeader}>
                <h2 className="text-sm font-semibold text-text">Relatórios</h2>
              </div>
              <div className="divide-y divide-border">
                {REPORT_ORDER.map((id) => {
                  const report = reports[id];
                  if (!report) return null;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setActiveReport(id)}
                      className={`flex w-full items-start gap-3 px-4 py-3 text-left transition ${
                        activeReport === id ? 'bg-primary-light/60' : 'hover:bg-slate-50'
                      }`}
                    >
                      <FileText size={16} className="mt-0.5 shrink-0 text-primary" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-text">{report.title}</div>
                        <div className="mt-0.5 text-xs text-muted">{report.rows?.length || 0} registro(s)</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Panel>

            <Panel>
              <div className={`${ui.panelHeader} flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between`}>
                <div>
                  <h2 className="text-lg font-semibold text-text">{current?.title}</h2>
                  <p className="text-sm text-muted">{current?.description}</p>
                </div>
                <button
                  type="button"
                  className={ui.btnPrimary}
                  disabled={!current || printing === current.id}
                  onClick={() => handlePrint(current.id)}
                >
                  <Printer size={16} />
                  {printing === current?.id ? 'Gerando PDF...' : 'Exportar PDF'}
                </button>
              </div>

              {!current || current.rows.length === 0 ? (
                <EmptyState
                  message="Nenhum registro neste relatório"
                  hint="Ajuste o período ou o status e busque novamente."
                />
              ) : (
                <TableShell>
                  <DataTable>
                    <thead className={ui.tableHead}>
                      <tr>
                        {current.columns.map((column) => (
                          <th key={column.key} className={ui.tableHeadCell}>{column.label}</th>
                        ))}
                        {current.id === 'rides' && (
                          <th className={`${ui.tableHeadCell} text-right`}>Roteiro</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {current.rows.map((row, index) => (
                        <tr key={`${current.id}-${index}`} className={`${ui.tableRow} ${index % 2 ? ui.tableRowZebra : ''}`}>
                          {current.columns.map((column) => (
                            <td key={column.key} className={ui.tableCell}>
                              {row[column.key] ?? '-'}
                            </td>
                          ))}
                          {current.id === 'rides' && (
                            <td className={`${ui.tableCell} text-right`}>
                              <button
                                type="button"
                                className={ui.btnGhost}
                                disabled={printing === `roteiro-${row.id}`}
                                onClick={() => handlePrintRoteiro(row.id)}
                                title="Imprimir roteiro desta corrida"
                              >
                                <Printer size={15} />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                </TableShell>
              )}
            </Panel>
          </div>
        )}
      </div>
    </PageShell>
  );
}
